import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { useMemo } from "react";
import type { ChartRow } from "../types";
import { dateFullFormatter } from "./_dates";
import { humanizeColumn } from "./_humanize";
import { formatWithUnits, unitsFromColumnName } from "./_units";

// ag-grid v32 ships ClientSideRowModel as part of the
// `ag-grid-community` package (packages mode). Manually calling
// ModuleRegistry.registerModules here would mix it with the
// per-module imports — ag-grid logs "you can only use one or the
// other" to the console. Dropping the registration relies on the
// package-mode auto-registration.

interface Props {
  rows: ChartRow[];
  columns: string[];
}

export function TableRenderer({ rows, columns }: Props) {
  const rowData = useMemo(
    () =>
      rows.map((row) => {
        const obj: Record<string, unknown> = {};
        columns.forEach((c, i) => {
          obj[c] = row[i];
        });
        return obj;
      }),
    [rows, columns],
  );

  const colDefs = useMemo(
    () =>
      columns.map((c, idx) => {
        // Per-column type detection — drives the cell formatter so
        // "2026-04-28" displays as "Apr 28, 2026" and "1296000" as
        // "1,296,000" without an explicit type hint from the model.
        const values = rows.map((r) => r[idx]);
        const dateFormatter = dateFullFormatter(values);
        // Units suffix on the column alias wins over the generic numeric
        // formatter — `*_amount` → "$2,476.48", `*_percent` → "24.5%",
        // `*_count` → "1,296". Fall back to generic numeric formatting
        // when there's no suffix signal.
        const units = !dateFormatter ? unitsFromColumnName(c) : null;
        const numericFormatter = !dateFormatter
          ? units
            ? (v: unknown) => formatWithUnits(v, units)
            : numericColumnFormatter(values)
          : null;
        const def: Record<string, unknown> = {
          field: c,
          headerName: humanizeColumn(c),
          sortable: true,
          filter: true,
          resizable: true,
        };
        if (dateFormatter) {
          def.valueFormatter = (params: { value: unknown }) => dateFormatter(params.value);
        } else if (numericFormatter) {
          def.valueFormatter = (params: { value: unknown }) => numericFormatter(params.value);
          def.type = "numericColumn";
          def.cellStyle = { textAlign: "right" } as Record<string, string>;
        }
        return def;
      }),
    [rows, columns],
  );

  return (
    <div className="ag-theme-quartz" style={{ width: "100%", height: 380 }}>
      <AgGridReact rowData={rowData} columnDefs={colDefs} />
    </div>
  );
}

/**
 * Build a thousands-separator formatter for a column when every
 * non-null value is numeric. Integer columns drop decimals; float
 * columns keep up to two decimal places. Returns null when the
 * column isn't numeric (text, mixed, all-null) so we leave display
 * untouched.
 */
function numericColumnFormatter(values: unknown[]): ((v: unknown) => string) | null {
  let sawNumber = false;
  let hasFractional = false;
  for (const v of values) {
    if (v == null || v === "") continue;
    const n = typeof v === "number" ? v : Number(v);
    if (!Number.isFinite(n)) return null;
    sawNumber = true;
    if (!Number.isInteger(n)) hasFractional = true;
  }
  if (!sawNumber) return null;
  const opts: Intl.NumberFormatOptions = hasFractional
    ? { minimumFractionDigits: 0, maximumFractionDigits: 2 }
    : { maximumFractionDigits: 0 };
  const fmt = new Intl.NumberFormat("en-US", opts);
  return (v: unknown) => {
    if (v == null || v === "") return "";
    const n = typeof v === "number" ? v : Number(v);
    if (!Number.isFinite(n)) return String(v);
    return fmt.format(n);
  };
}
